import React, { useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import noop from 'lodash/noop';
import { t, Trans } from '@lingui/macro';
import { Checkbox, Tag, Tooltip } from '@decisiv/ui-components';
import ArrowRight from '@decisiv/iconix/lib/components/ArrowRight';
import CheckCircleF from '@decisiv/iconix/lib/components/CheckCircleF';
import LinkIcon from '@decisiv/iconix/lib/components/Link';

import linkedIntegrations from '../../../utils/linkedIntegrations';
import { VisuallyHidden } from '../../../components/StyledComponents';
import { linkSurface } from './clicks';
import { formatTimestamp } from '../../../utils/dates';
import {
  audienceLabel,
  circuitBreakerOpen,
  networkSummary,
  resilienceFlags,
  triggerLabels,
} from './integrationFacts';
import {
  Card,
  CardBody,
  CardFooter,
  CardLink,
  CardName,
  ChipRow,
  Description,
  Facts,
  Muted,
  OpenHint,
  Raised,
  StatusDot,
  TitleRow,
} from './style';

const TriggerTags = ({ integration }) => (
  <ChipRow>
    {triggerLabels(integration).map(({ id, text, detail }) => (
      <span key={id} title={detail}>
        <Tag text={text} color="information" variant="outline" />
      </span>
    ))}
  </ChipRow>
);

TriggerTags.propTypes = {
  integration: PropTypes.oneOfType([PropTypes.object]).isRequired,
};

// The "clickable-card" Home layout: the whole card is the link, the name is
// plain text and the classic facts stay.
export default function IntegrationCard({
  integration,
  integrationId = '',
  locationUrl,
  allSharedSettings = [],
  selectable = false,
  selected = false,
  selectionLimitReached = false,
  onToggleSelect = noop,
}) {
  const linked = useMemo(
    () => linkedIntegrations(integrationId, allSharedSettings),
    [integrationId, allSharedSettings],
  );
  const breakerOpen = circuitBreakerOpen(integration);
  const network = networkSummary(integration);
  const flags = resilienceFlags(integration);
  const statusText = integration.enabled ? t`Active` : t`Inactive`;
  const cardLink = useRef(null);
  // Raised regions sit above the card link for hover details; clicks on them
  // behave like clicks on that link.
  const open = linkSurface(
    () => cardLink.current?.href,
    () => cardLink.current?.click(),
  );

  return (
    <Card
      data-selected={selected}
      data-alert={breakerOpen}
      aria-label={integration.name}
    >
      <CardLink
        ref={cardLink}
        to={locationUrl}
        text={<VisuallyHidden>{t`Open ${integration.name}`}</VisuallyHidden>}
      />
      {selectable && (
        <Raised justifyContent="flex-end" paddingX={1.5} paddingTop={1}>
          <Checkbox
            name={`select-integration-${integrationId || integration.name}`}
            aria-label={t`Select ${integration.name}`}
            value="selected"
            checked={selected}
            disabled={selectionLimitReached && !selected}
            onChange={() => onToggleSelect()}
          />
        </Raised>
      )}

      <CardBody>
        <TitleRow>
          <StatusDot
            data-enabled={Boolean(integration.enabled)}
            role="img"
            aria-label={statusText}
          />
          <CardName>{integration.name}</CardName>
          <OpenHint aria-hidden>
            <ArrowRight size="small" />
          </OpenHint>
        </TitleRow>

        <Description>{integration.description}</Description>

        <Facts>
          <dt>
            <Trans>Category</Trans>
          </dt>
          <dd>{integration.category}</dd>
          <dt>
            <Trans>Audience</Trans>
          </dt>
          <dd>{audienceLabel(integration)}</dd>
          <dt>
            <Trans>Network</Trans>
          </dt>
          <dd>
            {network.text}
            {network.rest.length > 0 && (
              <>
                {' '}
                <Tooltip
                  target={
                    <Muted tabIndex={0} {...open}>
                      +{network.rest.length}
                    </Muted>
                  }
                >
                  {network.rest.join(', ')}
                </Tooltip>
              </>
            )}
          </dd>
          <dt>
            <Trans>Trigger</Trans>
          </dt>
          <dd>
            <Raised {...open}>
              <TriggerTags integration={integration} />
            </Raised>
          </dd>
        </Facts>
      </CardBody>

      {(breakerOpen || flags.length > 0 || linked.length > 0) && (
        <CardFooter>
          {breakerOpen ? (
            <Raised {...open}>
              <Tooltip
                target={<Tag text={t`Circuit Breaker open`} color="danger" />}
              >
                {formatTimestamp(integration.circuit_breaker_opened_at)}
              </Tooltip>
            </Raised>
          ) : (
            flags.map(({ id, text }) => (
              <Tag
                key={id}
                text={text}
                palette="pastel"
                icon={CheckCircleF}
                color="success"
              />
            ))
          )}
          {linked.length > 0 && (
            <Raised {...open}>
              <Tooltip
                target={
                  <Tag
                    icon={LinkIcon}
                    text={t`${linked.length} linked`}
                    color="information"
                    variant="outline"
                  />
                }
              >
                {linked.map((item) => item.name).join(', ')}
              </Tooltip>
            </Raised>
          )}
        </CardFooter>
      )}
    </Card>
  );
}

IntegrationCard.propTypes = {
  integration: PropTypes.oneOfType([PropTypes.object]).isRequired,
  integrationId: PropTypes.string,
  locationUrl: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  allSharedSettings: PropTypes.array,
  selectable: PropTypes.bool,
  selected: PropTypes.bool,
  selectionLimitReached: PropTypes.bool,
  onToggleSelect: PropTypes.func,
};
